<script setup lang="ts">
import { ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuth } from '@/composables/useAuth'
import { useEditions } from '@/composables/useEditions'
import EditionForm from '@/components/organizer/events/EditionForm.vue'
import CancellationPolicyPanel from '@/components/organizer/CancellationPolicyPanel.vue'
import SwitchPolicyPanel from '@/components/organizer/SwitchPolicyPanel.vue'
import OrganizerLayout from '@/components/layout/OrganizerLayout.vue'
import type { StoreEditionPayload, UpdateEditionPayload } from '@/types/events'

interface EditionFormData {
  name: string
  start_date: string
  end_date: string
  venue_id: number | null
  active: boolean
}

function isAxiosError(e: unknown): e is { response: { status: number; data: { errors?: Record<string, string[]>; message?: string } } } {
  return (
    typeof e === 'object' &&
    e !== null &&
    'response' in e &&
    typeof (e as { response: unknown }).response === 'object' &&
    (e as { response: unknown }).response !== null
  )
}

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const { currentOrganisation } = useAuth()
const { createEdition } = useEditions()

const eventSlug = route.params['eventSlug'] as string

const step = ref<1 | 2 | 3>(1)
const createdEditionId = ref<number | null>(null)
const createdEditionSlug = ref<string | null>(null)

const loading = ref(false)
const errors = ref<Record<string, string[]>>({})
const generalError = ref('')

const form = ref<EditionFormData>({
  name: '',
  start_date: '',
  end_date: '',
  venue_id: null,
  active: false,
})

async function handleStep1Submit(data: StoreEditionPayload | UpdateEditionPayload): Promise<void> {
  errors.value = {}
  generalError.value = ''
  loading.value = true
  try {
    const edition = await createEdition(eventSlug, data as StoreEditionPayload)
    createdEditionId.value = edition.id
    createdEditionSlug.value = edition.slug
    step.value = 2
  } catch (e: unknown) {
    if (isAxiosError(e)) {
      if (e.response.status === 422 && e.response.data.errors) {
        errors.value = e.response.data.errors
      } else {
        generalError.value = t('editions.form.error_generic')
      }
    } else {
      generalError.value = t('editions.form.error_generic')
    }
  } finally {
    loading.value = false
  }
}

function finish(): void {
  void router.push(`/organizer/events/${eventSlug}/editions/${createdEditionSlug.value}`)
}
</script>

<template>
  <OrganizerLayout>
    <main class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div class="mb-6">
        <RouterLink
          :to="`/organizer/events/${eventSlug}`"
          class="text-sm text-slate-400 hover:text-white transition-colors inline-flex items-center gap-1"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
          {{ t('editions.detail.back_to_event') }}
        </RouterLink>
      </div>

      <!-- Step indicator -->
      <div class="mb-8">
        <ol class="flex items-center gap-0">
          <li
            v-for="(s, index) in [
              t('editions.wizard.step_details'),
              t('editions.wizard.step_cancellation'),
              t('editions.wizard.step_switch_race'),
            ]"
            :key="index"
            class="flex items-center"
            :class="index < 2 ? 'flex-1' : ''"
          >
            <div class="flex items-center gap-2 shrink-0">
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-colors"
                :class="
                  step > index + 1
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : step === index + 1
                      ? 'border-emerald-500 text-emerald-400 bg-transparent'
                      : 'border-slate-700 text-slate-600 bg-transparent'
                "
              >
                <svg v-if="step > index + 1" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                </svg>
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span
                class="text-sm font-medium hidden sm:block"
                :class="step === index + 1 ? 'text-white' : step > index + 1 ? 'text-emerald-400' : 'text-slate-600'"
              >
                {{ s }}
              </span>
            </div>
            <div
              v-if="index < 2"
              class="flex-1 h-px mx-3"
              :class="step > index + 1 ? 'bg-emerald-500' : 'bg-slate-700'"
            />
          </li>
        </ol>
      </div>

      <!-- Step 1: Edition details -->
      <template v-if="step === 1">
        <div class="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 sm:p-8">
          <h1 class="text-2xl font-bold text-white mb-6">{{ t('editions.form.create_heading') }}</h1>

          <div
            v-if="generalError"
            class="mb-6 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
          >
            {{ generalError }}
          </div>

          <EditionForm
            v-model="form"
            :loading="loading"
            :errors="errors"
            :organisation-id="currentOrganisation?.id ?? 0"
            :is-edit="false"
            :submit-label="t('editions.wizard.next')"
            @submit="handleStep1Submit"
            @cancel="router.push(`/organizer/events/${eventSlug}`)"
          />
        </div>
      </template>

      <!-- Step 2: Cancellation policy -->
      <template v-else-if="step === 2 && createdEditionId !== null">
        <CancellationPolicyPanel :edition-id="createdEditionId" />

        <div class="mt-6 flex items-center justify-between">
          <p class="text-sm text-slate-500">{{ t('editions.wizard.step_hint_optional') }}</p>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="step = 3"
            >
              {{ t('editions.wizard.skip') }}
            </button>
            <button
              type="button"
              class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="step = 3"
            >
              {{ t('editions.wizard.continue') }}
            </button>
          </div>
        </div>
      </template>

      <!-- Step 3: Switch race policy -->
      <template v-else-if="step === 3 && createdEditionId !== null">
        <SwitchPolicyPanel :edition-id="createdEditionId" />

        <div class="mt-6 flex items-center justify-between">
          <p class="text-sm text-slate-500">{{ t('editions.wizard.step_hint_optional') }}</p>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="step = 2"
            >
              {{ t('editions.wizard.back') }}
            </button>
            <button
              type="button"
              class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="finish"
            >
              {{ t('editions.wizard.skip') }}
            </button>
            <button
              type="button"
              class="bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors"
              @click="finish"
            >
              {{ t('editions.wizard.finish') }}
            </button>
          </div>
        </div>
      </template>
    </main>
  </OrganizerLayout>
</template>
